import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserCheck,
  Shield,
  Loader2,
  Trash2,
  Pencil,
  Save,
  X,
} from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(true);

  const [editing, setEditing] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("admin");

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const loadTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };

    loadTheme();
    window.addEventListener("theme-change", loadTheme);
    return () => window.removeEventListener("theme-change", loadTheme);
  }, []);

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "users"));
    setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ================= ACTIONS ================= */
  const approveUser = async (id) => {
    await updateDoc(doc(db, "users", id), { approved: true });
    fetchUsers();
  };

  const deleteUser = async (id) => {
    if (!window.confirm("This will permanently delete the user. Continue?"))
      return;
    await deleteDoc(doc(db, "users", id));
    fetchUsers();
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setEditEmail(u.email);
    setEditRole(u.role || "admin");
  };

  const saveEdit = async () => {
    await updateDoc(doc(db, "users", editing), {
      email: editEmail,
      role: editRole,
    });
    setEditing(null);
    fetchUsers();
  };

  const pending = users.filter((u) => !u.approved);
  const approved = users.filter((u) => u.approved);

  /* ================= THEME MAP ================= */
  const theme = {
    page: dark ? "bg-[#0b0b0b] text-white" : "bg-gray-100 text-gray-900",
    card: dark ? "bg-white/5 border-white/10" : "bg-white border-gray-200",
    muted: dark ? "text-gray-400" : "text-gray-600",
    border: dark ? "border-white/10" : "border-gray-200",
    input: dark
      ? "bg-black/40 border-white/20 text-white"
      : "bg-white border-gray-300",
  };

  return (
    <div className={`min-h-screen p-6 ${theme.page}`}>
      {/* ================= HEADER ================= */}
      <div className="max-w-5xl mx-auto mb-10">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Shield /> Admin User Management
        </h1>
        <p className={`mt-2 text-sm ${theme.muted}`}>
          Approve, edit, or remove administrative access
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        {loading && (
          <div className="flex items-center gap-3 py-20">
            <Loader2 className="animate-spin" />
            <span className={theme.muted}>Loading users…</span>
          </div>
        )}

        {!loading && (
          <>
            {/* ================= PENDING ================= */}
            <Section title="Pending Approval">
              {pending.length === 0 && <Empty text="No pending admins" />}
              {pending.map((u) => (
                <UserCard key={u.id} theme={theme}>
                  <span>{u.email}</span>

                  <div className="flex gap-2">
                    <ActionBtn
                      color="green"
                      icon={UserCheck}
                      onClick={() => approveUser(u.id)}
                      label="Approve"
                    />
                    <ActionBtn
                      color="red"
                      icon={Trash2}
                      onClick={() => deleteUser(u.id)}
                      label="Delete"
                    />
                  </div>
                </UserCard>
              ))}
            </Section>

            {/* ================= APPROVED ================= */}
            <Section title="Approved Admins">
              {approved.length === 0 && (
                <Empty text="No approved admins yet" />
              )}
              {approved.map((u) => (
                <UserCard key={u.id} theme={theme}>
                  <span>{u.email}</span>

                  <div className="flex gap-2">
                    <ActionBtn
                      color="blue"
                      icon={Pencil}
                      onClick={() => openEdit(u)}
                      label="Edit"
                    />
                    <ActionBtn
                      color="red"
                      icon={Trash2}
                      onClick={() => deleteUser(u.id)}
                      label="Delete"
                    />
                  </div>
                </UserCard>
              ))}
            </Section>
          </>
        )}
      </div>

      {/* ================= EDIT MODAL ================= */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className={`w-full max-w-md rounded-2xl p-6 ${theme.card}`}
            >
              <h3 className="text-xl font-semibold mb-4">Edit Admin</h3>

              <label className="text-sm mb-1 block">Email</label>
              <input
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className={`w-full mb-4 px-3 py-2 rounded-lg border ${theme.input}`}
              />

              <label className="text-sm mb-1 block">Role</label>
              <select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                className={`w-full mb-6 px-3 py-2 rounded-lg border ${theme.input}`}
              >
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 rounded-lg border flex items-center gap-1"
                >
                  <X size={16} /> Cancel
                </button>

                <button
                  onClick={saveEdit}
                  className="px-4 py-2 rounded-lg bg-sky-600 text-white flex items-center gap-1"
                >
                  <Save size={16} /> Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function Section({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function UserCard({ children, theme }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border p-4 flex justify-between items-center ${theme.card}`}
    >
      {children}
    </motion.div>
  );
}

function Empty({ text }) {
  return <p className="text-sm opacity-60">{text}</p>;
}

function ActionBtn({ icon: Icon, label, onClick, color }) {
  const colors = {
    green: "bg-emerald-600 hover:bg-emerald-700",
    red: "bg-red-600 hover:bg-red-700",
    blue: "bg-sky-600 hover:bg-sky-700",
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1 rounded-lg text-white text-sm ${colors[color]}`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}
